import React, { useEffect, useState } from 'react';
import { PurchaseorderDataForm } from '../../../../app/models/PurchaseOrder';
import SelectTwo, { SelectTwoMapperOptions } from '../../../../components/SelectTwo';
import { PaymentMethod } from '../../../../app/models/PaymentMethod';
import { Currency } from '../../../../app/models/Currency';
import { PaymentCondition } from '../../../../app/models/PaymentCondition';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import { ExchangeRateDate } from '../../../../app/models/ExchangeRateDate';
import { ExchangeRateDateEnum } from '../../../../app/shared/enums';

interface GeneralPurchaseOrderProps {
  purchaseOrderDataForm: PurchaseorderDataForm;
  setPurchaseOrderDataForm: (purchaseOrder: PurchaseorderDataForm) => void;
  paymentMethods: PaymentMethod[];
  currencies: Currency[];
  paymentConditions: PaymentCondition[];
  exchangeRateDate: ExchangeRateDate[];
  isShow: boolean;
  errorFields?: any;
}

const PurchaseOrderInvoicingForm = ({
  purchaseOrderDataForm,
  setPurchaseOrderDataForm,
  paymentMethods,
  currencies,
  paymentConditions,
  exchangeRateDate,
  errorFields,
  isShow = true,
}: GeneralPurchaseOrderProps) => {
  const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors, errors } = useHandleErrorFields();
  const [showFixedDateField, setShowFixedDateField] = useState(false);

  useEffect(() => {
    setShowFixedDateField(purchaseOrderDataForm.exchange_rate_date === ExchangeRateDateEnum.FECHA_FIJA);
  }, [purchaseOrderDataForm.exchange_rate_date]);

  useEffect(() => {
    setErrors(errorFields);
  }, [errorFields]);

  const handleChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setPurchaseOrderDataForm({ ...purchaseOrderDataForm, [name]: value });
  };

  const handleChangeSelectTwo = (name: string, value: number | string | null) => {
    const obj = { [name]: value };
    if (name === 'society_id') {
      setErrors({ ...errors, provider_id: '' });
    }
    setPurchaseOrderDataForm({ ...purchaseOrderDataForm, ...obj });
  };

  return (
    <>
      {/* Forma de pago y moneda */}
      <div className="mb-2 row">
        <div className="col-md-6">
          <label className="col-form-label">Forma de pago</label>
          <SelectTwo
            name="payment_method_id"
            id="payment_method_id"
            inputValue={purchaseOrderDataForm.payment_method_id}
            options={SelectTwoMapperOptions(paymentMethods, 'id', ['name'])}
            hasError={fieldHasError('payment_method_id') !== ''}
            onChange={(value: any) => handleChangeSelectTwo('payment_method_id', value.value)}
            placeholder="Selec. forma de pago"
            onFocus={() => onFocusRemove('payment_method_id')}
            disabled={isShow}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>
            {fieldErrorMessage('payment_method_id')}
          </div>
        </div>

        <div className="col-md-6">
          <label className="col-form-label">Moneda</label>
          <SelectTwo
            name="currency_id"
            id="currency_id"
            inputValue={purchaseOrderDataForm.currency_id}
            options={SelectTwoMapperOptions(currencies, 'id', ['name'])}
            hasError={fieldHasError('currency_id') !== ''}
            onChange={(value: any) => handleChangeSelectTwo('currency_id', value.value)}
            placeholder="Seleccione moneda"
            onFocus={() => onFocusRemove('currency_id')}
            disabled={isShow}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>
            {fieldErrorMessage('currency_id')}
          </div>
        </div>
      </div>

      {/* Condición de pago y días de pago */}
      <div className="mb-2 row">
        <div className="col-md-6">
          <label className="col-form-label">Condición de pago</label>
          <SelectTwo
            name="payment_condition"
            id="payment_condition"
            inputValue={purchaseOrderDataForm.payment_condition}
            hasError={fieldHasError('payment_condition') !== ''}
            options={SelectTwoMapperOptions(paymentConditions)}
            onChange={(value: any) => handleChangeSelectTwo('payment_condition', value.value)}
            onFocus={() => onFocusRemove('payment_condition')}
            placeholder="Seleccione cond. Pago"
            disabled={isShow}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>
            {fieldErrorMessage('payment_condition')}
          </div>
        </div>

        <div className="col-md-6">
          <label className="col-form-label">Cantidad de días</label>
          <input
            name="days_count"
            id="days_count"
            type="number"
            className={`form-control ${fieldHasError('days_count')}`}
            value={purchaseOrderDataForm.days_count}
            onChange={handleChange}
            onFocus={() => onFocusRemove('days_count')}
            placeholder="Días"
            disabled={isShow}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>
            {fieldErrorMessage('days_count')}
          </div>
        </div>
      </div>

      {/* Fecha tipo de cambio */}
      <div className="mb-2 row">
        <div className="col-md-6">
          <label className="col-form-label">Fecha tipo de cambio</label>
          <SelectTwo
            name="exchange_rate_date"
            id="exchange_rate_date"
            inputValue={purchaseOrderDataForm.exchange_rate_date}
            hasError={fieldHasError('exchange_rate_date') !== ''}
            options={SelectTwoMapperOptions(exchangeRateDate)}
            onChange={(value: any) => handleChangeSelectTwo('exchange_rate_date', value.value)}
            onFocus={() => onFocusRemove('exchange_rate_date')}
            placeholder="Seleccione fecha tipo de cambio"
            disabled={isShow}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>
            {fieldErrorMessage('exchange_rate_date')}
          </div>
        </div>
      </div>

      {/* Fecha fija o lógica de fecha calculada */}
      {showFixedDateField ? (
        <div className="mb-2 row">
          <div className="col-md-6">
            <label className="col-md-12 col-form-label">Fecha fija</label>
            <input
              type="date"
              name="fixed_date"
              className={`form-control ${fieldHasError('fixed_date')}`}
              value={purchaseOrderDataForm.fixed_date}
              onChange={handleChange}
              placeholder="Ingrese fecha"
              onFocus={() => onFocusRemove('fixed_date')}
            />
            <div className="invalid-feedback" style={{ display: 'flex' }}>
              {fieldErrorMessage('fixed_date')}
            </div>
          </div>
        </div>
      ) : (
        <div className="row mt-2">
            <div className="col-md-6">
            <div className="row">
                <div className="col-4">
                <label className="form-label">Días</label>
                <input
                    type="number"
                    name="final_days"
                    className={`form-control ${fieldHasError('final_days') ? 'is-invalid' : ''}`}
                    value={purchaseOrderDataForm.final_days}
                    onChange={handleChange}
                    onFocus={() => onFocusRemove('final_days')}
                    disabled={isShow}
                />
                <div className="invalid-feedback d-block">
                    {fieldErrorMessage('final_days')}
                </div>
                </div>

                <div className="col-4">
                <label className="form-label">Meses</label>
                <input
                    type="number"
                    name="final_months"
                    className={`form-control ${fieldHasError('final_months') ? 'is-invalid' : ''}`}
                    value={purchaseOrderDataForm.final_months}
                    onChange={handleChange}
                    onFocus={() => onFocusRemove('final_months')}
                    disabled={isShow}
                />
                <div className="invalid-feedback d-block">
                    {fieldErrorMessage('final_months')}
                </div>
                </div>

                <div className="col-4">
                <label className="form-label">Año</label>
                <input
                    type="number"
                    name="final_year"
                    className={`form-control ${fieldHasError('final_year') ? 'is-invalid' : ''}`}
                    value={purchaseOrderDataForm.final_year}
                    onChange={handleChange}
                    onFocus={() => onFocusRemove('final_year')}
                    disabled={isShow}
                />
                <div className="invalid-feedback d-block">
                    {fieldErrorMessage('final_year')}
                </div>
                </div>
            </div>
            </div>
        </div>
      )}
    </>
  );
};

export default PurchaseOrderInvoicingForm;
